import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  TrendingUp, 
  TrendingDown, 
  Link2, 
  MousePointerClick, 
  Mail,
  Target,
  DollarSign,
  BarChart3,
  Globe,
  Building2,
  Users
} from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const StatCard = ({ title, value, change, icon: Icon, prefix = "", suffix = "", color = "blue" }) => {
  const isPositive = change >= 0;
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-amber-50 text-amber-600"
  };

  return (
    <Card className="stat-card" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-slate-500">{title}</CardTitle>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          {prefix}{typeof value === 'number' ? value.toLocaleString('pt-PT') : value}{suffix}
        </div>
        {change !== undefined && change !== null && (
          <div className="flex items-center gap-1 mt-2">
            {isPositive ? (
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={isPositive ? "change-positive" : "change-negative"}>
              {isPositive ? "+" : ""}{change}%
            </span>
            <span className="text-xs text-slate-500 ml-1">vs período anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("all");
  const [seoReports, setSeoReports] = useState([]);
  const [adsReports, setAdsReports] = useState([]);
  const [emailReports, setEmailReports] = useState([]);

  const isAdmin = user?.role === "admin";

  // Fetch clients for admin dropdown
  useEffect(() => {
    if (isAdmin) {
      fetchClients();
    }
  }, [isAdmin]);

  // Fetch reports when client selection changes
  useEffect(() => {
    fetchData();
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const clientParam = selectedClient && selectedClient !== "all" ? `?client_id=${selectedClient}` : "";
      const [seoRes, adsRes, emailRes] = await Promise.all([
        axios.get(`${API_URL}/reports/seo${clientParam}`),
        axios.get(`${API_URL}/reports/ads${clientParam}`),
        axios.get(`${API_URL}/reports/email${clientParam}`)
      ]);
      setSeoReports(seoRes.data);
      setAdsReports(adsRes.data);
      setEmailReports(emailRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get selected client data
  const selectedClientData = clients.find(c => c.id === selectedClient);

  // Get latest reports
  const latestSeo = seoReports[0];
  const previousSeo = seoReports[1];
  const latestAds = adsReports[0];
  const previousAds = adsReports[1];
  const latestEmail = emailReports[0];
  const previousEmail = emailReports[1];

  // Calculate changes
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return parseFloat((((current - previous) / previous) * 100).toFixed(1));
  };

  // SEO changes
  const backlinksChange = latestSeo && previousSeo
    ? calculateChange(latestSeo.total_backlinks_acquired || 0, previousSeo.total_backlinks_acquired || 0)
    : null;

  // Ads changes
  const conversionsChange = latestAds && previousAds
    ? calculateChange(latestAds.conversions || 0, previousAds.conversions || 0)
    : null;

  // Email changes
  const openRateChange = latestEmail && previousEmail
    ? calculateChange(latestEmail.open_rate || 0, previousEmail.open_rate || 0)
    : null;

  // Calculate ROAS
  const calculateROAS = (r) => {
    if (!r || !r.total_spend || r.total_spend === 0) return 0;
    return (r.revenue || 0) / r.total_spend;
  };

  // Chart data
  const seoChartData = seoReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    backlinks: r.total_backlinks_acquired || 0,
    domains: r.new_referring_domains || 0
  }));

  const adsChartData = adsReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    spend: r.total_spend || 0,
    revenue: r.revenue || 0
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="stat-card">
              <CardContent className="pt-6">
                <div className="skeleton h-4 w-24 mb-3" />
                <div className="skeleton h-8 w-32 mb-2" />
                <div className="skeleton h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin && selectedClient === "all" 
              ? "Visão geral de todos os clientes"
              : `Bem-vindo, ${user?.name}. Aqui está um resumo da sua performance.`
            }
          </p>
        </div>

        {/* Admin Client Selector */}
        {isAdmin && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Users className="w-4 h-4" />
              <span>Filtrar por cliente:</span>
            </div>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-64" data-testid="client-selector">
                <SelectValue placeholder="Selecione um cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    Todos os clientes
                  </div>
                </SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {client.company}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Selected Client Badge */}
      {isAdmin && selectedClient !== "all" && selectedClientData && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <Building2 className="w-5 h-5 text-blue-600" />
          <div>
            <p className="font-semibold text-slate-900">{selectedClientData.company}</p>
            <p className="text-sm text-slate-500">{selectedClientData.name} • {selectedClientData.email}</p>
          </div>
          <Badge variant="outline" className="ml-auto badge-info">
            {seoReports.length + adsReports.length + emailReports.length} relatórios
          </Badge>
        </div>
      )}

      {/* No data message for clients */}
      {!isAdmin && seoReports.length === 0 && adsReports.length === 0 && emailReports.length === 0 && (
        <Card className="stat-card">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Ainda não há relatórios disponíveis</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              A First Media trabalha com SEO, Google Ads e Email Marketing. 
              Os seus relatórios aparecerão aqui assim que forem inseridos pela nossa equipa.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Main Stats - Only show sections with data */}
      {(seoReports.length > 0 || adsReports.length > 0 || emailReports.length > 0 || isAdmin) && (
        <>
          {/* SEO Stats - Show only if has SEO data or is admin */}
          {(seoReports.length > 0 || isAdmin) && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-600" />
                SEO
                {seoReports.length > 0 && (
                  <Badge variant="outline" className="text-xs">{seoReports.length} relatórios</Badge>
                )}
              </h2>
              {seoReports.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Total Backlinks"
                    value={latestSeo?.total_backlinks_acquired || 0}
                    change={backlinksChange}
                    icon={Link2}
                    color="blue"
                  />
                  <StatCard
                    title="DA Médio"
                    value={(latestSeo?.avg_domain_authority || 0).toFixed(1)}
                    icon={BarChart3}
                    color="blue"
                  />
                  <StatCard
                    title="Novos Domínios"
                    value={latestSeo?.new_referring_domains || 0}
                    icon={Globe}
                    color="blue"
                  />
                  <StatCard
                    title="Cliques"
                    value={latestSeo?.clicks || 0}
                    icon={MousePointerClick}
                    color="blue"
                  />
                </div>
              ) : (
                <Card className="stat-card bg-slate-50">
                  <CardContent className="py-6 text-center text-slate-500">
                    Sem dados de SEO disponíveis
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Ads Stats - Show only if has Ads data or is admin */}
          {(adsReports.length > 0 || isAdmin) && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <MousePointerClick className="w-5 h-5 text-amber-600" />
                Google Ads
                {adsReports.length > 0 && (
                  <Badge variant="outline" className="text-xs">{adsReports.length} relatórios</Badge>
                )}
              </h2>
              {adsReports.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Conversões"
                    value={latestAds?.conversions || 0}
                    change={conversionsChange}
                    icon={Target}
                    color="orange"
                  />
                  <StatCard
                    title="ROAS"
                    value={calculateROAS(latestAds).toFixed(2)}
                    icon={DollarSign}
                    suffix="x"
                    color="orange"
                  />
                  <StatCard
                    title="Total Spend"
                    value={(latestAds?.total_spend || 0).toFixed(2)}
                    icon={DollarSign}
                    suffix="€"
                    color="orange"
                  />
                  <StatCard
                    title="Revenue"
                    value={(latestAds?.revenue || 0).toFixed(2)}
                    icon={TrendingUp}
                    suffix="€"
                    color="green"
                  />
                </div>
              ) : (
                <Card className="stat-card bg-slate-50">
                  <CardContent className="py-6 text-center text-slate-500">
                    Sem dados de Google Ads disponíveis
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Email Stats - Show only if has Email data or is admin */}
          {(emailReports.length > 0 || isAdmin) && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Email Marketing
                {emailReports.length > 0 && (
                  <Badge variant="outline" className="text-xs">{emailReports.length} relatórios</Badge>
                )}
              </h2>
              {emailReports.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Open Rate"
                    value={(latestEmail?.open_rate || 0).toFixed(1)}
                    change={openRateChange}
                    icon={Mail}
                    suffix="%"
                    color="green"
                  />
                  <StatCard
                    title="Click Rate"
                    value={(latestEmail?.click_rate || 0).toFixed(1)}
                    icon={MousePointerClick}
                    suffix="%"
                    color="green"
                  />
                  <StatCard
                    title="Conversões"
                    value={latestEmail?.conversions || 0}
                    icon={Target}
                    color="green"
                  />
                  <StatCard
                    title="Revenue"
                    value={(latestEmail?.revenue || 0).toFixed(2)}
                    icon={DollarSign}
                    suffix="€"
                    color="green"
                  />
                </div>
              ) : (
                <Card className="stat-card bg-slate-50">
                  <CardContent className="py-6 text-center text-slate-500">
                    Sem dados de Email Marketing disponíveis
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {/* Charts Section - Only show if has data */}
      {(seoReports.length > 0 || adsReports.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SEO Backlinks Chart - Only if has SEO data */}
          {seoReports.length > 0 && (
            <Card className="stat-card" data-testid="seo-backlinks-chart">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                  <Link2 className="w-5 h-5 text-blue-600" />
                  Evolução de Backlinks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={seoChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="backlinks" fill="#2563EB" name="Backlinks" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="domains" fill="#10B981" name="Novos Domínios" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Ads Spend vs Revenue Chart - Only if has Ads data */}
          {adsReports.length > 0 && (
            <Card className="stat-card" data-testid="ads-performance-chart">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                  <MousePointerClick className="w-5 h-5 text-amber-600" />
                  Google Ads: Spend vs Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={adsChartData}>
                    <defs>
                      <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => `${value.toFixed(2)}€`}
                    />
                    <Area type="monotone" dataKey="spend" stroke="#F59E0B" strokeWidth={2} fill="url(#colorSpend)" name="Spend (€)" />
                    <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#colorRevenue)" name="Revenue (€)" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Latest Insights */}
      {(latestSeo?.notes || latestAds?.notes || latestEmail?.notes) && (
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Key Insights Recentes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {latestSeo?.notes && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-1">SEO</p>
                <p className="text-slate-700">{latestSeo.notes}</p>
              </div>
            )}
            {latestAds?.notes && (
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-1">Google Ads</p>
                <p className="text-slate-700">{latestAds.notes}</p>
              </div>
            )}
            {latestEmail?.notes && (
              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-sm font-medium text-emerald-800 mb-1">Email Marketing</p>
                <p className="text-slate-700">{latestEmail.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
